import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { tenantsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Trash, CircleNotch, Buildings } from '@phosphor-icons/react';

export default function TenantsPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: '', code: '' });

  const fetchTenants = async () => {
    try {
      const response = await tenantsAPI.getAll();
      setTenants(response.data);
    } catch (error) {
      toast.error(t('tenants.loadError'));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchTenants(); }, []);

  const handleCreateTenant = async () => {
    if (!newTenant.name || !newTenant.code) { toast.error(t('tenants.fillAll')); return; }
    setSaving(true);
    try {
      await tenantsAPI.create(newTenant);
      toast.success(t('tenants.created'));
      setCreateOpen(false);
      setNewTenant({ name: '', code: '' });
      fetchTenants();
    } catch (error) { toast.error(error.response?.data?.detail || t('tenants.createError')); }
    finally { setSaving(false); }
  };

  const handleDeleteTenant = async (e, tenantId, tenantName) => {
    e.stopPropagation();
    if (!window.confirm(`"${tenantName}" ${t('tenants.deleteConfirm')}`)) return;
    try { await tenantsAPI.delete(tenantId); toast.success(t('tenants.deleted')); fetchTenants(); }
    catch (error) { toast.error(t('tenants.deleteError')); }
  };

  return (
    <div data-testid="tenants-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">{t('tenants.title')}</h1>
          <p className="text-[#a1a1aa] text-sm mt-1">{tenants.length} {t('tenants.title')}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-tenant-btn" className="bg-[#D50C2D] hover:bg-[#f21d41]">
          <Plus size={18} /><span>{t('tenants.addTenant')}</span>
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><CircleNotch size={32} className="animate-spin text-[#D50C2D]" /></div>
      ) : tenants.length === 0 ? (
        <div className="text-center py-16 bg-[#141416] border border-white/10 rounded">
          <Buildings size={48} className="mx-auto text-[#52525b] mb-4" />
          <p className="text-[#a1a1aa]">{t('tenants.noTenants')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tenants.map((tenant) => (
            <div key={tenant.id}
              className="stat-card group cursor-pointer hover:border-white/20 transition-colors"
              onClick={() => navigate(`/tenants/${tenant.id}`)}
              data-testid={`tenant-card-${tenant.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#1C1C1F] rounded flex items-center justify-center">
                    <Buildings size={24} className="text-[#a1a1aa]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-white">{tenant.name}</h3>
                    <span className="tenant-badge mt-1">{tenant.code}</span>
                  </div>
                </div>
                <button
                  onClick={(e) => handleDeleteTenant(e, tenant.id, tenant.name)}
                  data-testid={`delete-tenant-${tenant.id}`}
                  className="p-2 text-[#52525b] hover:text-[#f87171] hover:bg-red-500/10 rounded transition-colors opacity-0 group-hover:opacity-100">
                  <Trash size={18} />
                </button>
              </div>
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#52525b]">{t('common.created')}</span>
                  <span className="text-[#a1a1aa]">{new Date(tenant.created_at).toLocaleDateString('de-DE')}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-[#52525b]">{t('common.status')}</span>
                  <span className={`status-badge ${tenant.is_active ? 'online' : 'offline'}`}>
                    {tenant.is_active ? t('common.active') : t('common.inactive')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">{t('tenants.newTenant')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#a1a1aa]">{t('common.name')} *</Label>
              <Input value={newTenant.name} onChange={(e) => setNewTenant(prev => ({ ...prev, name: e.target.value }))} placeholder="z.B. Europcar" className="bg-[#0A0A0B] border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-[#a1a1aa]">Code *</Label>
              <Input value={newTenant.code} onChange={(e) => setNewTenant(prev => ({ ...prev, code: e.target.value.toLowerCase().replace(/\s/g, '') }))} placeholder="z.B. europcar" className="bg-[#0A0A0B] border-white/10 mt-1 font-mono" />
              <p className="text-xs text-[#52525b] mt-1">{t('tenants.uniqueId')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-white/10">{t('common.cancel')}</Button>
            <Button onClick={handleCreateTenant} disabled={saving} className="bg-[#D50C2D] hover:bg-[#f21d41]">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
